import React, { useState } from 'react';
import { PageHeader } from '../../../survey_engine/SurveyTemplates/PageHeader';
import { Card } from '../../../../components/Card';
import { CardHeader } from '../../../survey_engine/SurveyTemplates/CardHeader';
import { getRedirectUriQueryParam } from '../../../../utils/url';
import { FormHeader } from '../components/FormHeader';
import { ReasonsInput } from '../components/ReasonsInput';
import { JobRequisitionReasonService } from '../../../../services/v1/applicant_tracking/JobRequisitionReasonService';
import { JobRequisitionReason } from '../../../../entities/v1/applicant_tracking/JobRequisitionReason';
import { Alert, AlertType } from '../../../../components/Alert';
import { IconSpan } from '../../../../components/IconSpan';
import { redirectWithAlert } from '../../../../services/FlashService';
import { Col } from 'reactstrap';

const REDIRECT_URI =
  getRedirectUriQueryParam() ??
  '/organization_settings/job_requisition_reasons';
const DUPLICATES_FOUND_ALERT_MESSAGE =
  'Some of the reasons already exist. Please change the name before proceeding.';
const SUCCESSFUL_ALERT_MESSAGE =
  'The requisition reasons were successfully created!';

export default function NewRejectedReasonPage() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reasons, setReasons] = useState<string[]>(['']);
  const [duplicates, setDuplicates] = useState<string[]>([]);
  const [alertActive, setAlertActive] = useState<boolean>(false);

  function isSavable() {
    return (
      reasons.length > 0 &&
      reasons.every((reason) => reason.length > 0) &&
      duplicates.every((duplicate) => !reasons.includes(duplicate))
    );
  }

  async function handleSave() {
    setIsLoading(true);
    const trimmedReasons = reasons.map((reason) => reason.trim());
    setReasons(trimmedReasons);
    try {
      await JobRequisitionReasonService.batchCreate(trimmedReasons);
      redirectWithAlert(REDIRECT_URI, SUCCESSFUL_ALERT_MESSAGE);
    } catch (error) {
      const response = error.body;
      if (response.code !== 'duplicated_job_requisition_reasons') throw error;

      setAlertActive(true);
      setDuplicates(response.body.map((r: JobRequisitionReason) => r.name));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {alertActive && (
        <Alert
          type={AlertType.Danger}
          clearable
          autoClearTimeout={4000}
          onClose={() => setAlertActive(false)}
        >
          <IconSpan
            spanText={DUPLICATES_FOUND_ALERT_MESSAGE}
            icon={{ name: 'bi-info-circle' }}
            spanClassName='ms-3'
          />
        </Alert>
      )}
      <div className='container'>
        <PageHeader title='Add Requisition Reason' backUrl={REDIRECT_URI} />
        <Card>
          <CardHeader
            title='Requisition Reason'
            isSavable={isSavable()}
            isLoading={isLoading}
            onSaveClick={handleSave}
            cancelUrl={REDIRECT_URI}
          />
          <Col xs={4}>
            <FormHeader
              mandatory
              title='Requisition reasons'
              constraint='Maximum 65 characters'
            />
            <ReasonsInput
              reasons={reasons}
              duplicates={duplicates}
              setReasons={setReasons}
            />
          </Col>
        </Card>
      </div>
    </>
  );
}
